import { createSlice } from '@reduxjs/toolkit'
import { Auth } from 'aws-amplify'
import { API } from 'src/utils/interceptor'
import Sentry from 'src/utils/sentry'

const initialState = {
  products: [],
  allManualProducts: [],
  isLoading: false,
  error: null,
  currentPage: 1,
  pageSize: 200,
  total: 0,
  hasMore: false,
  searchTerm: '',
  selectedSupplierId: null,
}

const ensureNominalCodeField = product => {
  if (!product || typeof product !== 'object') return product
  if (Object.prototype.hasOwnProperty.call(product, 'nominalCode')) {
    return product
  }
  return { ...product, nominalCode: '' }
}

const manualOrderProductsSlice = createSlice({
  name: 'manualOrderProducts',
  initialState,
  reducers: {
    setProducts: (state, action) => {
      state.products = action.payload
    },
    setAllManualProducts: (state, action) => {
      state.allManualProducts = action.payload
    },
    addProducts: (state, action) => {
      // Merge new products with existing ones, avoiding duplicates
      const existingIds = new Set(state.products.map(p => p._id))
      const newProducts = action.payload.filter(p => !existingIds.has(p._id))
      state.products = [...state.products, ...newProducts]
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setTotal: (state, action) => {
      state.total = action.payload
    },
    setHasMore: (state, action) => {
      state.hasMore = action.payload
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload
    },
    setSelectedSupplierId: (state, action) => {
      state.selectedSupplierId = action.payload
    },
    resetProducts: state => {
      state.products = []
      state.allManualProducts = []
      state.currentPage = 1
      state.total = 0
      state.hasMore = false
      state.searchTerm = ''
      state.selectedSupplierId = null
    },
    resetManualOrderProducts: () => {
      return initialState
    },
  },
})

export const {
  setProducts,
  setAllManualProducts,
  addProducts,
  setLoading,
  setError,
  setCurrentPage,
  setPageSize,
  setTotal,
  setHasMore,
  setSearchTerm,
  setSelectedSupplierId,
  resetProducts,
  resetManualOrderProducts,
} = manualOrderProductsSlice.actions

export const fetchSupplierProducts =
  ({
    venueId,
    supplierId,
    page = 1,
    pageSize = 200,
    searchTerm = '',
    append = false,
    isManualSupplier = false,
  }) =>
  async (dispatch, getState) => {
    try {
      console.log(
        'Fetching products for supplier:',
        supplierId,
        'Page:',
        page,
        'Search Term:',
        searchTerm,
        'Append:',
        append,
      )
      dispatch(setLoading(true))
      dispatch(setError(null))

      if (!append) {
        dispatch(resetProducts())
        dispatch(setSelectedSupplierId(supplierId))
      }

      let products = []
      let total = 0
      let currentPage = page
      let hasMore = false

      if (isManualSupplier) {
        let allManualProducts = []
        if (append) {
          const state = getState().manualOrderProducts
          allManualProducts = state?.allManualProducts || []
        }
        if (!append || allManualProducts.length === 0) {
          const manualProductsResponse = await API.get(
            'api',
            `venue/${venueId}/${supplierId}/products?includeManualOnly=true`,
          )
          allManualProducts = (manualProductsResponse || []).map(
            ensureNominalCodeField,
          )
          dispatch(setAllManualProducts(allManualProducts || []))
        }

        const normalizedSearch = searchTerm.trim().toLowerCase()
        const filteredManualProducts = (allManualProducts || []).filter(
          prod => {
            if (!normalizedSearch) return true
            return (
              String(prod?.title || '')
                .toLowerCase()
                .includes(normalizedSearch) ||
              String(prod?.sku || '')
                .toLowerCase()
                .includes(normalizedSearch)
            )
          },
        )

        total = filteredManualProducts.length
        const startIndex = (page - 1) * pageSize
        const endIndex = startIndex + pageSize
        products = filteredManualProducts.slice(startIndex, endIndex)
        hasMore = endIndex < total
      } else {
        const session = await Auth.currentAuthenticatedUser()

        // Use the catalogue API to fetch products for the specific supplier
        const requestBody = {
          searchTerm: searchTerm.trim(),
          searchFields: ['title', 'sku', 'supplierTitle'],
          searchPage: page,
          searchPerPage: pageSize,
          filterValues: [
            {
              field: 'supplier',
              value: supplierId,
              operator: 'equals',
            },
          ],
        }

        console.log('API Request Body:', requestBody)
        console.log('Supplier ID being sent:', supplierId)

        const searchResults = await API.post(
          'api',
          `venue/${venueId}/catalogue`,
          {
            body: requestBody,
            queryStringParameters: {
              username: session.attributes.sub,
              supplierId: supplierId, // Also send supplier ID as query parameter
            },
          },
        )

        products = (searchResults?.results || []).map(ensureNominalCodeField)
        total = searchResults?.total || 0
        currentPage = searchResults?.pageNumber || page
        hasMore = currentPage * pageSize < total
      }

      if (append) {
        dispatch(addProducts(products))
      } else {
        dispatch(setProducts(products))
      }

      dispatch(setTotal(total))
      dispatch(setCurrentPage(currentPage))
      dispatch(setHasMore(hasMore))
      dispatch(setSearchTerm(searchTerm))
      dispatch(setLoading(false))

      return { products, total, hasMore, currentPage }
    } catch (e) {
      console.error('Error fetching supplier products:', e)
      dispatch(setLoading(false))
      dispatch(setError(e.message))
      Sentry.captureException(e)
      return false
    }
  }

export const searchSupplierProducts =
  ({
    venueId,
    supplierId,
    searchTerm,
    page = 1,
    pageSize = 200,
    isManualSupplier = false,
  }) =>
  async dispatch => {
    return dispatch(
      fetchSupplierProducts({
        venueId,
        supplierId,
        searchTerm,
        page,
        pageSize,
        append: false,
        isManualSupplier,
      }),
    )
  }

export const loadMoreSupplierProducts =
  ({ venueId, supplierId, page, pageSize = 200, isManualSupplier = false }) =>
  async (dispatch, getState) => {
    const state = getState().manualOrderProducts
    return dispatch(
      fetchSupplierProducts({
        venueId,
        supplierId,
        searchTerm: state.searchTerm,
        page,
        pageSize,
        append: true,
        isManualSupplier,
      }),
    )
  }

export default manualOrderProductsSlice
